import React, { useEffect, useState } from "react";
import { ExternalLink, Music } from "lucide-react";
import api, { resolveImage } from "@/lib/api";
import { RichMultiline } from "@/lib/richText";
import Seo from "@/components/Seo";
import ShareButton from "@/components/ShareButton";

const LISTEN_DEFAULTS = {
  kicker: "Listen",
  heading: "Poetry, set to music.",
  description:
    "A handful of my poems — given voice and melody through Suno. Press play, settle in, and let the words sing.",
  empty_message:
    "The first song is on its way. Check back soon for poems set to music.",
};

const SongCard = ({ song }) => {
  const cover = song.cover_image ? resolveImage(song.cover_image) : null;
  const audio = song.audio_url ? resolveImage(song.audio_url) : null;
  // Use a server-rendered share URL so Facebook/iMessage/etc. can pick up
  // the song's cover image as the preview. Real visitors get redirected
  // to /listen#song-{id} instantly. Falls back to the anchor URL locally.
  const origin =
    typeof window !== "undefined" ? window.location.origin : "";
  const songUrl = `${origin}/api/share/song/${song.id}`;
  const trackedRef = React.useRef(false);
  const onPlay = () => {
    if (trackedRef.current) return; // once per page load per song
    trackedRef.current = true;
    api.post("/track/song", { song_id: song.id, kind: "play" }).catch(() => {});
  };
  return (
    <article
      id={`song-${song.id}`}
      data-testid={`song-card-${song.id}`}
      className="border border-rule bg-paper-2/30 overflow-hidden flex flex-col md:flex-row scroll-mt-24"
    >
      {cover && (
        <div className="md:w-72 shrink-0 bg-paper-2/60 flex items-center justify-center">
          <img
            src={cover}
            alt={song.title}
            className="w-full h-full object-cover aspect-square md:aspect-auto"
          />
        </div>
      )}
      <div className="flex-1 p-7 lg:p-9 flex flex-col gap-4">
        <div className="flex items-center gap-2 text-terracotta">
          <Music size={14} />
          <p className="text-[11px] uppercase tracking-[0.22em]">A song from a poem</p>
        </div>

        <h2 className="font-serif-display text-3xl text-ink leading-tight">
          {song.title}
        </h2>

        {song.description && (
          <p className="text-sm text-ink-soft leading-relaxed italic">
            {song.description}
          </p>
        )}

        {audio ? (
          <audio
            controls
            preload="none"
            src={audio}
            onPlay={onPlay}
            data-testid={`song-player-${song.id}`}
            className="w-full mt-2"
          >
            Your browser does not support the audio element.
          </audio>
        ) : (
          <p className="text-xs text-ink-soft italic">
            Audio coming soon — please use the Suno link below to listen.
          </p>
        )}

        <div className="flex flex-wrap items-center gap-4 mt-2">
          <span
            onClick={() => {
              api.post("/track/song", { song_id: song.id, kind: "share" }).catch(() => {});
            }}
          >
            <ShareButton
              title={`${song.title} — A poem set to music by Barbara Ruffing`}
              excerpt={song.description || "A poem of mine, given voice and melody."}
              url={songUrl}
              label="Share song"
              className="inline-flex items-center gap-2 px-4 py-2 bg-ink text-paper text-[11px] tracking-[0.2em] uppercase hover:bg-terracotta transition-colors"
            />
          </span>

          {song.suno_url && (
            <a
              href={song.suno_url}
              target="_blank"
              rel="noopener noreferrer"
              data-testid={`song-suno-link-${song.id}`}
              className="inline-flex items-center gap-2 text-xs uppercase tracking-[0.2em] text-ink-soft hover:text-terracotta"
            >
              <ExternalLink size={12} /> Listen on Suno
            </a>
          )}
        </div>
      </div>
    </article>
  );
};

export default function Listen() {
  const [songs, setSongs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [copy, setCopy] = useState(LISTEN_DEFAULTS);

  useEffect(() => {
    api
      .get("/pages")
      .then((r) => setCopy({ ...LISTEN_DEFAULTS, ...(r.data?.listen || {}) }))
      .catch(() => {});
  }, []);

  useEffect(() => {
    (async () => {
      try {
        const { data } = await api.get("/songs");
        setSongs(data || []);
      } finally {
        setLoading(false);
      }
    })();
  }, []);

  // After songs render, if URL has a #song-xxx hash, smooth-scroll to it.
  useEffect(() => {
    if (loading || songs.length === 0) return;
    if (typeof window === "undefined") return;
    const hash = window.location.hash || "";
    if (!hash.startsWith("#song-")) return;
    const el = document.getElementById(hash.slice(1));
    if (el) {
      setTimeout(() => {
        el.scrollIntoView({ behavior: "smooth", block: "start" });
      }, 80);
    }
  }, [loading, songs]);

  return (
    <div className="bg-paper min-h-[80vh]" data-testid="listen-page">
      <Seo
        title="Listen — B. Ruff Writes"
        description={copy.description}
      />
      <div className="max-w-5xl mx-auto px-6 lg:px-10 pt-16 lg:pt-24 pb-24">
        <header className="border-b border-rule pb-10 mb-12">
          <p className="text-xs uppercase tracking-[0.25em] text-terracotta mb-4">
            {copy.kicker}
          </p>
          <h1 className="font-serif-display text-5xl lg:text-6xl text-ink tracking-tight leading-[1.05]">
            <RichMultiline text={copy.heading} />
          </h1>
          <p className="mt-6 text-base text-ink-soft max-w-2xl leading-relaxed">
            {copy.description}
          </p>
          {songs.length > 0 && (
            <div className="mt-8">
              <ShareButton
                title="Listen — Poems set to music by Barbara Ruffing"
                excerpt={copy.description}
                label="Share this page"
                className="inline-flex items-center gap-2 px-5 py-3 text-xs tracking-[0.18em] uppercase text-ink-soft hover:text-terracotta transition-colors"
              />
            </div>
          )}
        </header>

        {loading ? (
          <p className="text-ink-soft" data-testid="listen-loading">Loading…</p>
        ) : songs.length === 0 ? (
          <div
            data-testid="listen-empty"
            className="border border-dashed border-rule bg-paper-2/40 p-12 text-center"
          >
            <p className="font-serif-display text-2xl italic text-ink-soft">
              {copy.empty_message}
            </p>
          </div>
        ) : (
          <div className="grid grid-cols-1 gap-8" data-testid="listen-grid">
            {songs.map((s) => (
              <SongCard key={s.id} song={s} />
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
